/**
 * AI conversational surface shells — presentation only.
 * No provider calls, no mutation, no authority/approval grants.
 */

import React from 'react';
import { Tag, Typography } from 'antd';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from '../primitives/primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from '../primitives/PrimitiveSurfaceFrame';
import type { AiOpenContextSchemaV0, AiResultKindV0 } from './contracts';

const { Text, Paragraph } = Typography;

function assertNoAuthority(grantsAuthority?: false, grantsApproval?: false): void {
  const a = grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const b = grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (a !== false || b !== false) {
    throw new Error('AI conversational shells must keep grantsAuthority/grantsApproval=false');
  }
}

const RESULT_KIND_LABEL_KO: Record<AiResultKindV0, string> = {
  explanation: '설명',
  summary: '요약',
  proposal: '제안',
  draft: '초안',
  execution_request: '실행 요청',
  execution_result: '실행 결과',
  evidence: '증빙',
  processing_record: '처리 기록',
};

export interface AIAssistancePanelProps {
  readonly presentationState: PrimitivePresentationState;
  readonly title?: string;
  readonly guidance?: string | null;
  readonly resultKind?: AiResultKindV0;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AIAssistancePanel(props: AIAssistancePanelProps): React.ReactElement {
  assertNoAuthority(props.grantsAuthority, props.grantsApproval);
  return (
    <PrimitiveSurfaceFrame
      title={props.title ?? 'AI 지원'}
      presentationState={props.presentationState}
      emptyMessage="AI 기능을 사용할 수 없습니다"
      data-testid={props['data-testid'] ?? 'ai-assistance-panel'}
      aria-label="AI 지원"
    >
      {props.resultKind ? (
        <Tag color="default">{RESULT_KIND_LABEL_KO[props.resultKind]}</Tag>
      ) : null}
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {props.guidance?.trim()
          ? props.guidance
          : '대화는 안내만 담당합니다. 실행은 별도 권한 확인 후에만 진행됩니다.'}
      </Paragraph>
      {/* Machine claim flags stay on DOM data-* via parent stub / frame — not user copy */}
    </PrimitiveSurfaceFrame>
  );
}

export interface AIContextSummaryProps {
  readonly presentationState: PrimitivePresentationState;
  readonly context?: Partial<AiOpenContextSchemaV0> | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AIContextSummary(props: AIContextSummaryProps): React.ReactElement {
  assertNoAuthority(props.grantsAuthority, props.grantsApproval);
  const c = props.context ?? {};
  const scopeHint =
    c.layer === 'genesis'
      ? '조직 시작 화면에 맞는 안내 범위입니다.'
      : c.layer === 'portal'
        ? '선택한 거래 관계 범위의 안내입니다.'
        : '현재 화면에 맞는 안내 범위입니다.';
  return (
    <PrimitiveSurfaceFrame
      title="AI 컨텍스트"
      presentationState={props.presentationState}
      emptyMessage="표시할 AI 컨텍스트가 없습니다"
      data-testid={props['data-testid'] ?? 'ai-context-summary'}
      aria-label="AI 컨텍스트"
    >
      <div
        hidden
        aria-hidden="true"
        data-ai-layer={c.layer ?? ''}
        data-ai-role={c.role ?? ''}
        data-ai-tenant={c.tenantId ?? ''}
        data-ai-relation={c.relationId ?? ''}
        data-ai-read-scope={c.allowedReadScope ?? ''}
        data-ai-propose-scope={c.allowedProposeScope ?? ''}
        data-ai-execute-scope={c.allowedExecuteScope ?? ''}
        data-ai-approval-required={c.approvalRequired == null ? '' : String(c.approvalRequired)}
      />
      <Text type="secondary">{scopeHint}</Text>
    </PrimitiveSurfaceFrame>
  );
}

export interface AIProposalCardProps {
  readonly presentationState: PrimitivePresentationState;
  readonly title?: string;
  readonly summary?: string | null;
  readonly resultKind?: 'proposal' | 'draft';
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AIProposalCard(props: AIProposalCardProps): React.ReactElement {
  assertNoAuthority(props.grantsAuthority, props.grantsApproval);
  const kind = props.resultKind ?? 'proposal';
  return (
    <PrimitiveSurfaceFrame
      title={props.title ?? 'AI 제안'}
      presentationState={props.presentationState}
      emptyMessage="표시할 제안이 없습니다."
      data-testid={props['data-testid'] ?? 'ai-proposal-card'}
      aria-label="AI 제안"
    >
      <Tag color="processing">{RESULT_KIND_LABEL_KO[kind]}</Tag>
      <Paragraph style={{ marginBottom: 0 }}>
        {props.summary?.trim() ?? '제안은 실행이 아닙니다. 미리보기·승인·확인 후에만 실행됩니다.'}
      </Paragraph>
    </PrimitiveSurfaceFrame>
  );
}

export interface AIActionPreviewProps {
  readonly presentationState: PrimitivePresentationState;
  readonly previewLabel?: string | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AIActionPreview(props: AIActionPreviewProps): React.ReactElement {
  assertNoAuthority(props.grantsAuthority, props.grantsApproval);
  return (
    <PrimitiveSurfaceFrame
      title="실행 미리보기"
      presentationState={props.presentationState}
      emptyMessage="미리보기할 mutation이 없습니다."
      data-testid={props['data-testid'] ?? 'ai-action-preview'}
      aria-label="AI 실행 미리보기"
    >
      <Tag>{RESULT_KIND_LABEL_KO.execution_request}</Tag>
      <Paragraph style={{ marginBottom: 0 }}>
        {props.previewLabel?.trim() ?? '권한 검사 후 mutation preview만 표시합니다 (실행 아님).'}
      </Paragraph>
    </PrimitiveSurfaceFrame>
  );
}

export interface AIApprovalRequirementProps {
  readonly presentationState: PrimitivePresentationState;
  readonly approvalRequired?: boolean | null;
  readonly note?: string | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AIApprovalRequirement(props: AIApprovalRequirementProps): React.ReactElement {
  assertNoAuthority(props.grantsAuthority, props.grantsApproval);
  return (
    <PrimitiveSurfaceFrame
      title="승인 요구"
      presentationState={props.presentationState}
      emptyMessage="승인 요구 정보가 없습니다."
      data-testid={props['data-testid'] ?? 'ai-approval-requirement'}
      aria-label="AI 승인 요구"
    >
      <Text>
        approvalRequired={props.approvalRequired == null ? '—' : String(props.approvalRequired)}
      </Text>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {props.note?.trim() ??
          'AI는 승인 결정을 대체하지 않습니다. APPROVE_DENY_DECISION_AUTHORITY=EQUAL 유지.'}
      </Paragraph>
    </PrimitiveSurfaceFrame>
  );
}

export interface AISourceAndEvidenceProps {
  readonly presentationState: PrimitivePresentationState;
  readonly sources?: readonly { id: string; label: string }[];
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AISourceAndEvidence(props: AISourceAndEvidenceProps): React.ReactElement {
  assertNoAuthority(props.grantsAuthority, props.grantsApproval);
  const sources = props.sources ?? [];
  return (
    <PrimitiveSurfaceFrame
      title="출처·증빙"
      presentationState={props.presentationState}
      emptyMessage="연결된 출처/증빙이 없습니다."
      data-testid={props['data-testid'] ?? 'ai-source-and-evidence'}
      aria-label="AI 출처 및 증빙"
    >
      <Tag>{RESULT_KIND_LABEL_KO.evidence}</Tag>
      {sources.length === 0 ? (
        <Text type="secondary">서버가 인정한 출처만 표시합니다 (날조 금지).</Text>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {sources.map((s) => (
            <li key={s.id}>{s.label}</li>
          ))}
        </ul>
      )}
    </PrimitiveSurfaceFrame>
  );
}

export interface AIExecutionReceiptProps {
  readonly presentationState: PrimitivePresentationState;
  readonly receiptLabel?: string | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AIExecutionReceipt(props: AIExecutionReceiptProps): React.ReactElement {
  assertNoAuthority(props.grantsAuthority, props.grantsApproval);
  return (
    <PrimitiveSurfaceFrame
      title="실행 영수증"
      presentationState={props.presentationState}
      emptyMessage="실행 영수증이 없습니다 (실행 전에는 비어 있음)."
      data-testid={props['data-testid'] ?? 'ai-execution-receipt'}
      aria-label="AI 실행 영수증"
    >
      <Tag color="success">{RESULT_KIND_LABEL_KO.execution_result}</Tag>
      <Paragraph style={{ marginBottom: 0 }}>
        {props.receiptLabel?.trim() ?? '실제 runtime execute 이후에만 영수증을 표시합니다.'}
      </Paragraph>
    </PrimitiveSurfaceFrame>
  );
}

export interface AIHandoffPanelProps {
  readonly presentationState: PrimitivePresentationState;
  readonly handoffLabel?: string | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

export function AIHandoffPanel(props: AIHandoffPanelProps): React.ReactElement {
  assertNoAuthority(props.grantsAuthority, props.grantsApproval);
  return (
    <PrimitiveSurfaceFrame
      title="핸드오프"
      presentationState={props.presentationState}
      emptyMessage="핸드오프 안내가 없습니다."
      data-testid={props['data-testid'] ?? 'ai-handoff-panel'}
      aria-label="AI 핸드오프"
    >
      <Paragraph style={{ marginBottom: 0 }}>
        {props.handoffLabel?.trim() ??
          'Portal/관계 스코프 다음 행동 안내. 내부 스태프 노트·타 거래상대방 데이터 금지.'}
      </Paragraph>
    </PrimitiveSurfaceFrame>
  );
}
